import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ArrowRight, Lightbulb, Loader2 } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

interface StepDecisionInputProps {
  initialValue?: string;
  onSubmit: (description: string) => void;
  isClassifying?: boolean;
}

const EXAMPLES = [
  "Sollen wir unser Pricing-Modell auf Usage-Based umstellen oder beim Abo bleiben?",
  "Wir müssen entscheiden, ob wir jetzt einen VP Sales einstellen oder erst den Product-Market-Fit vertiefen.",
  "Unser B2B-Produkt funktioniert gut, aber Enterprise-Kunden verlangen Features, die unser SMB-Produkt kannibalisieren könnten.",
];

export const StepDecisionInput = ({
  initialValue = "",
  onSubmit,
  isClassifying = false,
}: StepDecisionInputProps) => {
  const [value, setValue] = useState(initialValue);
  const [showExamples, setShowExamples] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus textarea on mount
    setTimeout(() => textareaRef.current?.focus(), 200);
  }, []);

  const isValid = value.trim().length >= 20;

  const handleSubmit = () => {
    if (isValid && !isClassifying) {
      onSubmit(value.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && isValid && !isClassifying) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleExampleClick = (example: string) => {
    setValue(example);
    setShowExamples(false);
    textareaRef.current?.focus();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-3">
        <h1 className="text-2xl md:text-[36px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
          Welche Entscheidung steht an?
        </h1>
        <p className="text-base text-muted-foreground">
          Beschreibe kurz, worum es geht. Das System erkennt automatisch den passenden Entscheidungsbereich.
        </p>
      </div>

      <div className="space-y-3">
        <TextareaAutosize
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="z.B. Sollen wir unser Pricing-Modell umstellen oder den Fokus auf Enterprise-Kunden verschieben?"
          minRows={3}
          maxRows={8}
          maxLength={1000}
          disabled={isClassifying}
          className={cn(
            "flex w-full rounded-xl border border-input bg-background px-4 py-4 text-base leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-[15px] resize-none transition-all",
            isClassifying && "opacity-60"
          )}
        />

        <div className="flex items-center justify-between">
          <span className={cn(
            "text-xs transition-colors",
            value.trim().length < 20 ? "text-muted-foreground" : "text-primary"
          )}>
            {value.trim().length}/1000 Zeichen
            {value.trim().length > 0 && value.trim().length < 20 && (
              <span className="ml-1 text-muted-foreground">(mind. 20)</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setShowExamples(!showExamples)}
            className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Lightbulb className="w-3 h-3" />
            {showExamples ? "Ausblenden" : "Beispiele"}
          </button>
        </div>
      </div>

      {/* Examples */}
      {showExamples && (
        <div className="space-y-2 animate-fade-in">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Beispiele</p>
          {EXAMPLES.map((example, i) => (
            <button
              key={i}
              onClick={() => handleExampleClick(example)}
              className="w-full text-left p-3 rounded-lg border border-border/60 bg-card/30 hover:bg-card/60 hover:border-primary/30 transition-all text-sm text-muted-foreground hover:text-foreground"
            >
              „{example}"
            </button>
          ))}
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!isValid || isClassifying}
        className="h-12 px-6 text-base font-semibold w-full sm:w-auto gap-2"
      >
        {isClassifying ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Wird analysiert...
          </>
        ) : (
          <>
            Weiter
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </Button>
    </div>
  );
};
